// ◉ xlVISION · Powering Business Together!
// © ExcelVision, Lda. — Braga, Portugal
// ( ·:·●●·:· ) xlVISION | Powering Business Together!
//
// ============================================================================
// A porta — entrada no sistema (ADR-004 §2.1-bis)
// ============================================================================
//
// ⚠️ **PORQUE É QUE O ECRÃ VEM ANTES DE FECHAR AS ESCRITAS, e não depois.**
//   Fechar `POST /api/plano/reservar` sem haver forma de entrar tornava o sistema
//   **inutilizável** — o planeador ficava com um mapa que lê e não escreve, sem sítio onde
//   se identificar e sem mensagem que explicasse porquê. As duas coisas saem juntas.
//
// ⚠️ **TRÊS ESTADOS E NÃO DOIS**, e o terceiro é o que costuma faltar:
//   · **com sessão** → o mapa;
//   · **sem sessão, com contas** → entrar;
//   · **sem sessão e SEM CONTAS** → arrancar o sistema, criando a primeira.
//
//   Sem o terceiro, um sistema recém instalado mostra um formulário de entrada que ninguém
//   no mundo pode preencher, e quem o instalou conclui que está avariado. Foi a mesma razão
//   que levou a API a devolver a contagem de contas no `401`.
//
// ⚠️ **O SSO Microsoft não está aqui.** A costura existe na base (`metodo_entrada` admite
//   `microsoft`) e falta o `tenant_id`, o `client_id` e um segredo que a GVA tem de criar no
//   Entra ID. Escrever o fluxo sem o poder correr uma única vez seria entregar código não
//   verificado no caminho de autenticação — o último sítio onde isso é aceitável.
//   📗 2026-10-19 · o botão desactivado **saiu**, com a nota que o explicava: *«o entrar
//   microsoft desaparecer do login, ficando sem efeito para já»*. Volta quando o fluxo existir.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { MudarSenha } from './mapa/Sessao.js';
import { entrarNaVersaoCerta } from './versao-da-pagina.js';

export interface Sessao {
  readonly autenticado: boolean;
  readonly utilizador: {
    readonly id: string;
    readonly email: string;
    readonly nome: string;
    readonly papel: string;
  } | null;
  readonly contas: number;
  readonly arranquePossivel?: boolean;
  readonly semBase?: boolean;
  /**
   * 🔴 2026-10-21 · a senha é a que o superadmin escolheu (conta criada ou redefinida). O servidor
   * recusa tudo excepto mudá-la (403 `senha_temporaria`), logo o mapa não pode carregar: o ecrã
   * leva a pessoa a mudá-la primeiro.
   */
  readonly senhaTemporaria?: boolean;
}

type Fase =
  | { readonly tipo: 'a_ler' }
  | { readonly tipo: 'sem_base' }
  | { readonly tipo: 'entrar' }
  | { readonly tipo: 'arrancar' }
  | { readonly tipo: 'dentro'; readonly sessao: Sessao }
  /** Entrou com a senha temporária: tem de a mudar antes do mapa. */
  | { readonly tipo: 'mudar_senha' }
  /** A primeira conta foi criada: mostra o segredo TOTP **uma vez**. */
  | { readonly tipo: 'segredo'; readonly uri: string; readonly qrSvg: string };

/** Extrai o segredo base32 do URI `otpauth://`, para se poder escrever à mão. */
export function segredoDoUri(uri: string): string {
  return /[?&]secret=([A-Z2-7]+)/i.exec(uri)?.[1] ?? '';
}

export function Entrada({
  children,
}: {
  /**
   * 🔴 2026-10-07 · o segundo argumento é o gesto «a sessão terminou»: quem recebe um 401 chama-o, e
   * a entrada relê a sessão e mostra o ecrã de entrar com a razão — em vez de a aplicação mostrar
   * «API inacessível: HTTP 401» com instruções de programador.
   */
  readonly children: (s: Sessao, sessaoTerminou: () => void) => React.ReactNode;
}) {
  const [fase, definirFase] = useState<Fase>({ tipo: 'a_ler' });
  /** A sessão acabou enquanto se usava a aplicação (inactividade, ou terminada noutro sítio). */
  const [sessaoAcabou, definirSessaoAcabou] = useState(false);
  const [erro, definirErro] = useState<string | null>(null);
  const [aEnviar, definirAEnviar] = useState(false);
  /**
   * Segundos que faltam do bloqueio por tentativas (A-5). `0` quando não há bloqueio.
   *
   * ⚠️ **PORQUE EXISTE.** A API já devolvia `429` com o tempo e um `Retry-After`, e o ecrã
   * mostrava só a mensagem — não distinguia «bloqueado» de «errado» nem contava os segundos.
   * Quem leva um bloqueio sem perceber **tenta mais**, e cada tentativa alonga-o. O ecrã
   * estava a agravar exactamente o que a limitação existe para travar.
   */
  const [esperaS, definirEsperaS] = useState(0);

  const [email, definirEmail] = useState('');
  const [senha, definirSenha] = useState('');
  const [totp, definirTotp] = useState('');
  const [nome, definirNome] = useState('');
  /**
   * O email escrito **segunda vez**.
   *
   * ⚠️ **PORQUE EXISTE, e não é zelo genérico.** O email é o identificador de entrada, e este
   * formulário **fecha-se para sempre** depois de criar a conta. Um erro de escrita aqui é um
   * bloqueio quase definitivo: a entrada não pode dizer «esse email não existe» (diria a um
   * atacante quais existem), logo quem não entra nunca descobre porquê. Aconteceu — a
   * primeira conta ficou com `pinhieiro` em vez de `pinheiro` e foram precisas duas
   * ferramentas de base de dados para o descobrir e corrigir.
   *
   * ⚠️ E é confirmação por **repetição** e não por `type="email"`: esse valida a FORMA, e
   * `pinhieiro@excelvision.pt` é um email perfeitamente bem formado.
   */
  const [emailConfirma, definirEmailConfirma] = useState('');
  const [segredoArranque, definirSegredoArranque] = useState('');

  const ler = useCallback(async () => {
    try {
      const r = await fetch('/api/auth/sessao');
      const s = (await r.json()) as Sessao;
      if (s.semBase === true) {
        definirFase({ tipo: 'sem_base' });
        return;
      }
      // ⚠️⚠️ **A senha temporária vem ANTES do mapa** — o servidor recusa o mapa com ela.
      if (s.autenticado && s.senhaTemporaria === true) {
        definirFase({ tipo: 'mudar_senha' });
        return;
      }
      if (s.autenticado) {
        // 🔴 2026-10-28 · Uma página de entrada de uma versão anterior recarrega aqui, antes de
        //    haver janela aberta — senão o primeiro clique recarregava e fechava a janela. Ver
        //    `versao-da-pagina.ts`.
        if (!(await entrarNaVersaoCerta())) return;
        definirFase({ tipo: 'dentro', sessao: s });
        return;
      }
      definirFase(s.contas === 0 ? { tipo: 'arrancar' } : { tipo: 'entrar' });
    } catch (e) {
      definirErro(e instanceof Error ? e.message : String(e));
      definirFase({ tipo: 'sem_base' });
    }
  }, []);

  useEffect(() => {
    void ler();
  }, [ler]);

  // A contagem decrescente. Um número que desce é a diferença entre «espera» e «avariado».
  useEffect(() => {
    if (esperaS <= 0) return;
    const t = setTimeout(() => definirEsperaS((n) => Math.max(0, n - 1)), 1_000);
    return () => clearTimeout(t);
  }, [esperaS]);

  const enviar = useCallback(
    async (caminho: string, corpo: unknown, aoConseguir: (c: unknown) => void) => {
      definirErro(null);
      definirAEnviar(true);
      try {
        const r = await fetch(caminho, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(corpo),
        });
        const c = (await r.json()) as { detalhe?: string; erro?: string };
        if (!r.ok) {
          definirErro(c.detalhe ?? c.erro ?? `a API respondeu ${r.status}`);
          // ⚠️ O 429 traz `Retry-After`. Mostrá-lo a descer é o que evita que quem está
          // bloqueado continue a tentar e alongue o próprio bloqueio.
          if (r.status === 429) {
            const espera = Number(r.headers?.get?.('retry-after') ?? '0');
            definirEsperaS(Number.isFinite(espera) && espera > 0 ? espera : 60);
          }
          return;
        }
        definirEsperaS(0);
        aoConseguir(c);
      } catch (e) {
        definirErro(e instanceof Error ? e.message : String(e));
      } finally {
        definirAEnviar(false);
      }
    },
    [],
  );

  if (fase.tipo === 'dentro') {
    return (
      <>
        {children(fase.sessao, () => {
          definirSessaoAcabou(true);
          void ler();
        })}
      </>
    );
  }

  if (fase.tipo === 'a_ler') {
    return (
      // 2026-10-19 · centrado, na mesma página da entrada: um texto encostado ao canto de cima
      // durante o arranque lia-se como página partida.
      <main className="pagina pagina--entrada">
        <p className="nota entrada-a-ler">a verificar a sessão…</p>
      </main>
    );
  }

  return (
    <main className="pagina pagina--entrada">
      {/* ⚠️ O LOGÓTIPO VEM DO REPOSITÓRIO e não de um URL externo (P-32). Um logótipo
          carregado de fora falha quando a rede falha, muda quando alguém muda o ficheiro
          remoto, e no ecrã de ENTRADA seria o primeiro elemento a partir-se. E mantém a
          proporção: `height` fixa e `width: auto`, nunca as duas. */}
      <header className="entrada-cab">
        <img className="entrada-logo" src="/logo-xlvision.png" alt="xlVISION" />
        <div>
          <h1>Mapa de Planeamento de Turismo</h1>
          <p className="subtitulo">Grupo Vale do Ave · Turismo</p>
        </div>
      </header>

      {fase.tipo === 'sem_base' ? (
        <section className="cartao">
          <h2>Sem base de dados</h2>
          <p className="erro">Não há base ligada, logo não há sessões.</p>
          <p className="nota">
            O estado das dependências está em <code>/api/health?detalhe</code>.
          </p>
        </section>
      ) : null}

      {/* ---- 🔴 A SENHA TEMPORÁRIA — mudá-la antes do mapa (2026-10-21) ------------
          A conta foi criada ou redefinida pelo superadmin, que lhe deu a senha: duas pessoas a
          sabem. O servidor recusa tudo o resto até ela mudar; o formulário é o mesmo do perfil
          (`MudarSenha`), com a mesma regra dos 12 caracteres, e ao gravar relê a sessão. */}
      {fase.tipo === 'mudar_senha' ? (
        <section className="cartao">
          <h2>Mude a senha temporária</h2>
          <p className="nota">
            Para continuar, escolha uma senha sua. A sessão actual mantém-se; o mapa abre logo a
            seguir.
          </p>
          <MudarSenha temporaria abertoDeInicio aoMudar={() => void ler()} />
          <p>
            <button
              type="button"
              className="btn"
              onClick={() => {
                void fetch('/api/auth/sair', { method: 'POST' })
                  .catch(() => undefined)
                  .then(() => ler());
              }}
            >
              Sair
            </button>
          </p>
        </section>
      ) : null}

      {/* ---- ARRANCAR: a primeira conta ------------------------------------ */}
      {fase.tipo === 'arrancar' ? (
        <section className="cartao">
          <h2>Arrancar o sistema</h2>
          <p className="nota">
            Ainda não existe nenhuma conta. A primeira é <strong>superadmin</strong> e exige o
            segredo de arranque, que está nas variáveis de ambiente do serviço — nunca na base
            de dados.
          </p>
          <form
            className="formulario"
            onSubmit={(e) => {
              e.preventDefault();
              if (email.trim().toLowerCase() !== emailConfirma.trim().toLowerCase()) return;
              void enviar(
                '/api/auth/primeiro',
                { email, nome, senha, segredo: segredoArranque },
                (c) =>
                  definirFase({
                    tipo: 'segredo',
                    uri: (c as { uriTotp: string }).uriTotp,
                    qrSvg: (c as { qrSvg?: string }).qrSvg ?? '',
                  }),
              );
            }}
          >
            <label>
              <span>Nome</span>
              <input value={nome} onChange={(e) => definirNome(e.target.value)} required />
            </label>
            <label>
              <span>Email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => definirEmail(e.target.value)}
                required
              />
            </label>
            <label>
              <span>Repetir o email — é o identificador de entrada e não se pode corrigir aqui</span>
              <input
                type="email"
                value={emailConfirma}
                onChange={(e) => definirEmailConfirma(e.target.value)}
                required
              />
            </label>
            {/* Dito ANTES de submeter, e não depois: um erro apontado a seguir ao envio já
                não se pode corrigir neste ecrã. */}
            {emailConfirma !== '' && emailConfirma.trim().toLowerCase() !== email.trim().toLowerCase() ? (
              <p className="erro">os dois emails não coincidem</p>
            ) : null}
            <label>
              {/* ⚠️ 12 e não 8: esta conta é superadmin e a porta é pública. O comprimento é
                  a única exigência — regras de composição produzem senhas piores e
                  previsíveis, e é o que o NIST SP 800-63B diz desde 2017. */}
              <span>Senha — pelo menos 12 caracteres</span>
              <input
                type="password"
                value={senha}
                onChange={(e) => definirSenha(e.target.value)}
                minLength={12}
                required
              />
            </label>
            <label>
              {/* ⚠️ **DIZ ONDE ESTÁ**, e faltava. O formulário pedia o segredo e não dizia
                  onde o encontrar — e um campo obrigatório sem indicação de origem é um
                  bloqueio, não uma verificação. Foi exactamente o que aconteceu ao cliente:
                  «não consigo fazer login… para superadmin não dá para escrever». */}
              <span>
                Segredo de arranque — variável <code>SEGREDO_ARRANQUE</code> do serviço, no
                painel do Railway (nunca na base de dados)
              </span>
              <input
                type="password"
                value={segredoArranque}
                onChange={(e) => definirSegredoArranque(e.target.value)}
                required
              />
            </label>
            {erro !== null ? <p className="erro">{erro}</p> : null}
            <button type="submit" className="btn btn--principal" disabled={aEnviar}>
              {aEnviar ? 'a criar…' : 'Criar a primeira conta'}
            </button>
          </form>
        </section>
      ) : null}

      {/* ---- O SEGREDO TOTP, uma vez --------------------------------------- */}
      {fase.tipo === 'segredo' ? (
        <section className="cartao">
          <h2>Conta criada — falta configurar o autenticador</h2>
          {/* ⚠️ MOSTRADO UMA VEZ E NUNCA MAIS. Não há endpoint que o volte a mostrar: um
              endpoint desses seria uma forma de contornar o TOTP sabendo só a senha. */}
          <p className="entrada-aviso-forte">
            Isto aparece <strong>uma só vez</strong>. Se fechar sem o fazer, a conta tem de ser
            apagada e recriada — não existe forma de recuperar.
          </p>

          {/* ⚠️ INSTRUÇÕES PASSO A PASSO, e não «escreva este segredo». A versão anterior
              assumia que quem lê sabe que tem de ACRESCENTAR UMA ENTRADA à aplicação — e o
              utilizador confundiu ter o Microsoft Authenticator instalado com a conta do
              trabalho ligada com ter esta inscrição feita. Escreveu códigos da Microsoft
              durante horas. A conta do trabalho gera códigos PARA A MICROSOFT. */}
          <ol className="entrada-passos">
            <li>
              Abrir a aplicação de autenticação no telemóvel (Microsoft Authenticator, Google
              Authenticator, 1Password…).
            </li>
            <li>
              <strong>Adicionar uma conta nova</strong> → «Outra conta» ou «Outra (chave)».
              ⚠️ Não serve a conta do trabalho que já lá está: essa gera códigos para a
              Microsoft, não para aqui.
            </li>
            <li>Ler este código com a câmara:</li>
          </ol>

          {/* Gerado no SERVIDOR e embutido: mandar o `otpauth://` — que contém o segredo —
              para um gerador de QR externo seria entregar o segundo factor a terceiros. */}
          {fase.qrSvg !== '' ? (
            <div
              className="qr-totp"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: fase.qrSvg }}
            />
          ) : null}

          <p className="nota">
            Sem câmara? Escolher «introduzir chave manualmente» e escrever:
          </p>
          <p className="segredo-totp">{segredoDoUri(fase.uri)}</p>
          <p className="nota">
            A aplicação passa a mostrar <strong>6 dígitos que mudam a cada 30 segundos</strong>.
            É esse número que se escreve ao entrar.
          </p>
          <button
            type="button"
            className="btn btn--principal"
            onClick={() => {
              definirSenha('');
              definirFase({ tipo: 'entrar' });
            }}
          >
            Já guardei — entrar
          </button>
        </section>
      ) : null}

      {/* ---- ENTRAR -------------------------------------------------------- */}
      {fase.tipo === 'entrar' ? (
        <section className="cartao">
          <h2>Entrar</h2>
          {sessaoAcabou ? (
            <p className="nota" role="status">
              A sua sessão terminou (por inactividade, ou foi terminada noutro sítio) — entre outra vez
              para continuar.
            </p>
          ) : null}
          <form
            className="formulario"
            onSubmit={(e) => {
              e.preventDefault();
              void enviar('/api/auth/entrar', { email, senha, totp }, () => {
                definirSenha('');
                definirTotp('');
                void ler();
              });
            }}
          >
            <label>
              <span>Email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => definirEmail(e.target.value)}
                required
                autoComplete="username"
                autoFocus
              />
            </label>
            <label>
              <span>Senha</span>
              <input
                type="password"
                value={senha}
                onChange={(e) => definirSenha(e.target.value)}
                required
                autoComplete="current-password"
              />
            </label>
            <label>
              {/* Condição A-4 do ADR-004: sem TOTP obrigatório nas contas por credenciais, o
                  MFA do tenant Microsoft fica DECORATIVO — a segurança do sistema é a da
                  porta mais fraca, e um atacante não escolhe a difícil. */}
              <span>Código do autenticador — 6 dígitos</span>
              <input
                className="entrada-totp"
                placeholder="000000"
                value={totp}
                onChange={(e) => definirTotp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                inputMode="numeric"
                autoComplete="one-time-code"
                required
              />
            </label>
            {erro !== null ? <p className="erro">{erro}</p> : null}
            {/* ⚠️ Bloqueado é DIFERENTE de errado, e tem de se ver: o botão desactiva-se e o
                número desce. Sem isto, quem está bloqueado tenta mais e alonga o bloqueio. */}
            {esperaS > 0 ? (
              <p className="entrada-espera">
                Demasiadas tentativas. Pode tentar dentro de <strong>{esperaS} s</strong>.
              </p>
            ) : null}
            <button
              type="submit"
              className="btn btn--principal"
              disabled={aEnviar || esperaS > 0}
            >
              {aEnviar ? 'a entrar…' : esperaS > 0 ? `aguardar ${esperaS} s` : 'Entrar'}
            </button>
          </form>
        </section>
      ) : null}
    </main>
  );
}
